import { useEffect, useRef } from 'react';
/**
 * Хук, возвращающий предыдущее значение `value` без перерисовки компонента.
 * Позволяет обнаружить изменение `value` и использовать предыдущее значение при запуске эффектов.
 *
 * NOTE: Предыдущее значение будет доступно в рендере и layout эффектах.
 * Если переложить значение в переменную, то предыдущее значение можно прокинуть в useEffect.
 *
 * @param value Текущее значение `value`
 * @param defaultValue Значение по умолчанию
 * @returns Предыдущее значение `value`
 * @example
 * ```ts
 * function Component({ name }: { name: string }) {
 *     const prevValue = usePreviousRef(name).current;
 *
 *     useEffect(() => {
 *         // При изменении пропса запускаем бизнес-логику, которую нельзя
 *         // выполнять при рендере компонента.
 *         if (name !== prevValue) {
 *             void someThunk({ name });
 *         }
 *     }, [prevValue, name]);
 *
 *     return <div>{name}</div>;
 * }
 * ```
 */
export function usePreviousRef(value, defaultValue = value) {
    const previousValue = useRef(defaultValue);
    useEffect(() => {
        previousValue.current = value;
    }, [value]);
    return previousValue;
}
